import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { GridApi } from "ag-grid-community";
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  ClickAwayListener,
  createFilterOptions,
  TextField,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { AutocompleteRenderInputParams } from "@mui/material/Autocomplete/Autocomplete";
import { useDialog } from "@/utils/hooks/useDialog.hook";
import { agGridGroupingSelectRenderOption } from "@/components/pages/common/ag-grid/components/grouping-select/agGridGroupingSelectRenderOption";
import { AgGridGroupingSelectTrigger } from "@/components/pages/common/ag-grid/components/grouping-select/AgGridGroupingSelectTrigger";

interface AgGridGroupingSelectProps {
  gridApi: GridApi | null;
  groupingOptions: Record<string, string>;
}

export const AgGridGroupingSelect: FC<AgGridGroupingSelectProps> = ({
  gridApi,
  groupingOptions,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const { open, openDialog, closeDialog } = useDialog();

  const options = useMemo(
    () => Object.keys(groupingOptions),
    [groupingOptions],
  );

  const filterOptions: AutocompleteProps<any, any, any, any>["filterOptions"] =
    useMemo(() => {
      return createFilterOptions({
        ignoreCase: true,
        stringify: (option) => groupingOptions[option] ?? `${option}`,
      });
    }, [groupingOptions]);

  const changeHandler = useCallback((_: unknown, newValues: string[]) => {
    setSelectedValues(newValues);
  }, []);

  const closeHandler = useCallback(() => {
    closeDialog();

    const needToUpdateGrid =
      getSelectedColumnsFromGridApi(gridApi).join("") !==
      selectedValues.join("");

    if (needToUpdateGrid) {
      gridApi?.setRowGroupColumns(selectedValues);
    }
  }, [gridApi, selectedValues, closeDialog]);

  const noneClickHandler = useCallback(() => {
    gridApi?.setRowGroupColumns([]);
    setSelectedValues([]);
    closeDialog();
  }, [gridApi, closeDialog]);

  const renderOption = useMemo(() => {
    return agGridGroupingSelectRenderOption(
      selectedValues,
      groupingOptions,
      noneClickHandler,
    );
  }, [selectedValues, groupingOptions, noneClickHandler]);

  useEffect(() => {
    gridApi?.addEventListener("columnRowGroupChanged", (event) => {
      if (event.type !== "columnRowGroupChanged") {
        return;
      }

      setSelectedValues(getSelectedColumnsFromGridApi(event?.api));
    });

    setSelectedValues(getSelectedColumnsFromGridApi(gridApi));
  }, [gridApi]);

  return (
    <Stack position="relative">
      <AgGridGroupingSelectTrigger
        open={open}
        openDialog={openDialog}
        selectedValues={selectedValues}
        groupingOptions={groupingOptions}
      />

      {open && (
        <Box sx={wrapperBoxStyles}>
          <ClickAwayListener onClickAway={closeHandler}>
            <div>
              <Autocomplete
                open
                multiple
                size="xsmall"
                options={options}
                disableCloseOnSelect
                onClose={closeHandler}
                value={selectedValues}
                onChange={changeHandler}
                noOptionsText="No labels"
                renderInput={renderInput}
                renderOption={renderOption}
                filterOptions={filterOptions}
              />
            </div>
          </ClickAwayListener>
        </Box>
      )}
    </Stack>
  );
};

const getSelectedColumnsFromGridApi = (api: GridApi | null) => {
  return api?.getRowGroupColumns()?.map((col) => col.getColId()) ?? [];
};

const wrapperBoxStyles = {
  top: 30,
  left: 0,
  position: "absolute",
  bgcolor: "white",
  zIndex: "modal",
  boxShadow: "1px 3px 21px -10px #000",
  "& .MuiPopper-root": {
    position: "relative !important",
    transform: "none !important",
  },
};

const renderInput = (params: AutocompleteRenderInputParams) => (
  <TextField
    size="xsmall"
    autoFocus
    placeholder="Search"
    ref={params.InputProps.ref}
    inputProps={params.inputProps}
    sx={{ minWidth: 250, p: 1 }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon />
        </InputAdornment>
      ),
    }}
  />
);
